import { SagaIterator } from 'redux-saga';
import { AnyAction } from 'redux';
import { call, cancel, put, select } from 'redux-saga/effects';
import {
  BasisStreckenState,
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  StreckenTarifierungRequestEvents,
  TARIF_CHECKED,
  ZUSATZOPTION_CHECKED,
  ZUSATZOPTION_UNCHECKED,
} from '@ps20-frontend/shop-integration-redux';
import { Feature, TaasRequestModel, TaasResponseModel } from '@ps20/bff-models';
import { App, areAppTypesEquivalent, getTarifbereich, VorvertragVorschaeden } from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getTaasVertragsoptionen,
  TaasData,
} from '../taasRequestHelper';
import {
  filterGesamtpreis,
  mapVorvertragVorschaeden,
  updateBausteineWithTarifierungsergebnis,
} from '../taasResponseHelper';
import { adresseNichtTarifierbar, handleTarifierungsReturnCodeInStrecke } from '../errorHandling';
import { setDeltafaktorenIfAvailable } from '../deltafaktorenHandler';
import { BasisTarifierer, ExtAxiosError, PricingRequestAction } from './basisTarifierer';

export interface TarfierungAppConfig {
  appId: App;
  successEventWithInfo: StreckenTarifierungRequestEvents;
  successEventWithoutInfo: StreckenTarifierungRequestEvents;
  errorEvent: StreckenTarifierungRequestEvents;
}

export class StreckenTarifierer<
  T extends BasisStreckenState,
  U extends TaasData,
> extends BasisTarifierer<T, U> {
  constructor(
    private readonly tarifierungAppConfig: TarfierungAppConfig,
    buildTaasData: (state: T) => U,
    shouldCancelRequest?: (state: T, action: AnyAction) => boolean,
    private readonly createRelevantRiskdataHash: (taasData: U) => string = createHashedTaasData,
  ) {
    super(tarifierungAppConfig.appId, buildTaasData, shouldCancelRequest);
  }

  *handleSuccess(successAction: DoRequestResponse<TaasResponseModel>): SagaIterator {
    // NOSONAR
    const state: T = yield select();
    const globalPut = GlobalPut.getInstance();
    const actionPayload = successAction.payload;
    const { angebotsId, appId } = successAction.doRequestAction.payload;

    if (appId !== this.appId || angebotsId !== state.angebotsId) {
      return yield cancel();
    }

    if (state.produkt == null) {
      yield call(globalPut.put, new GlobalActions().tarifierungHasEnded());
      return;
    }

    const tarifierteProduktlinien = updateBausteineWithTarifierungsergebnis(
      state,
      successAction.payload.produktlinien,
      appId,
    );
    yield call(
      globalPut.put,
      new GlobalActions().preiseStreckeErmittelt(
        this.appId,
        tarifierteProduktlinien,
        successAction.payload,
        state.angebotsId,
      ),
    );
    yield call(setDeltafaktorenIfAvailable, successAction.payload.deltafaktoren);

    if (areAppTypesEquivalent(appId, App.VWG)) {
      yield call(
        globalPut.put,
        new GlobalActions().updateRisikoortZuerszone(
          Number(successAction.payload?.elementarSB?.id),
          state.angebotsId,
        ),
      );
    }

    const showPreisChangedInfo =
      successAction.doRequestAction.payload.successEvent ===
      this.tarifierungAppConfig.successEventWithInfo;
    const gesamtpreis = filterGesamtpreis(actionPayload, state);
    yield call(
      globalPut.put,
      new GlobalActions().gesamtpreisChanged(
        gesamtpreis,
        showPreisChangedInfo,
        this.appId,
        angebotsId,
      ),
    );
    yield call(globalPut.put, new GlobalActions().tarifierungHasEnded());

    const vorvertragVorschaeden: VorvertragVorschaeden[] = mapVorvertragVorschaeden(actionPayload, {
      appId: this.appId,
      angebotsId: state.angebotsId,
    });
    if (vorvertragVorschaeden) {
      yield call(
        globalPut.put,
        new GlobalActions().vorvertragVorschaedenErmittelt(vorvertragVorschaeden),
      );
    }
  }

  *handleError(errorAction: DoRequestResponse<ExtAxiosError>): SagaIterator {
    const globalPut = GlobalPut.getInstance();
    const fehlerCode = errorAction.payload.response.data?.taasStatus?.fehlerCode;
    const state: T = yield select();
    const { angebotsId, appId } = errorAction.doRequestAction.payload;

    if (appId !== this.appId || angebotsId !== state.angebotsId) {
      return yield cancel();
    }
    const addressNotTarifierable = adresseNichtTarifierbar(fehlerCode, appId);
    if (addressNotTarifierable) {
      yield call(globalPut.put, new GlobalActions().tarifierungHasCancelled());
    }
    const actionResponsePayload = errorAction.payload.response.data;
    const originEvent = errorAction.doRequestAction.payload.originEvent;

    if (originEvent?.errorPolicy === 'report') {
      if (!addressNotTarifierable) {
        yield call(globalPut.put, new GlobalActions().tarifierungHasEnded());
      }
      yield call(
        globalPut.put,
        new GlobalActions().streckenPricingFailed({
          appId: this.appId,
          angebotsId,
          fehlerCode,
          requestId: originEvent.requestId,
        }),
      );
      return;
    }

    return yield call(
      handleTarifierungsReturnCodeInStrecke,
      actionResponsePayload.taasStatus,
      originEvent,
      this.appId,
      angebotsId,
    );
  }

  protected *callTaas(taasRequestModel: TaasRequestModel<U>, action: AnyAction) {
    const globalPut = GlobalPut.getInstance();
    yield call(globalPut.put, new GlobalActions().tarifierungHasStarted());
    yield* super.callTaas(taasRequestModel, action);
  }

  protected *buildTaasRequestModel(state: T): Generator<unknown, TaasRequestModel<U>, unknown> {
    const taasData = this.buildTaasData(state);

    const relevantRiskdataHash = this.createRelevantRiskdataHash(taasData);
    if (state.taasInfo.hashedRiskData !== relevantRiskdataHash) {
      yield put(new GlobalActions().changeRelevantRiskdataHash(relevantRiskdataHash));
    }

    return buildTaasRequestModelForStrecke(
      getTaasVertragsoptionen(state.vertragsoptionen, state.produkt.spartenType, {
        aaWorldTkvFT: state.transientState.featureToggles[Feature.AA_WORLD_TKV],
        aktion10FT: state.transientState.featureToggles[Feature.AKTION_10],
        tier10FT: state.transientState.featureToggles[Feature.TIER_10],
      }),
      taasData,
      getTarifbereich(state.versicherungsnehmer),
      state.vnWechselAngaben,
      state.taasInfo,
    );
  }

  protected getErrorEvent(): string {
    return this.tarifierungAppConfig.errorEvent.toString();
  }

  protected getSuccessEvent(action: PricingRequestAction): string {
    const hidePreisChangedInfo =
      action.errorPolicy === 'report' ||
      action.type === TARIF_CHECKED ||
      action.type === ZUSATZOPTION_CHECKED ||
      action.type === ZUSATZOPTION_UNCHECKED;
    return hidePreisChangedInfo
      ? this.tarifierungAppConfig.successEventWithoutInfo.toString()
      : this.tarifierungAppConfig.successEventWithInfo.toString();
  }
}
